import { useEffect } from 'react';
import { Fuel, Zap, Users, Leaf, Sun, Battery, Recycle } from 'lucide-react';
import { BrandHero, Prose, FeatureGrid, CtaBand, SectionHeader } from '@/components/brand/BrandKit';

const EV_IMG = "/generated/sustainability-ev.jpg";
const SOLAR_IMG = "/generated/sustainability-solar.jpg";

const pillars = [
  {
    icon: Fuel,
    title: "Fuel Efficiency",
    desc: "Our 3 A-Grade workshops maintain and optimize 12,000+ vehicles for fuel efficiency, reducing consumption and emissions across operations spanning all seven Emirates.",
    stat: "3",
    statLabel: "A-Grade workshop facilities",
  },
  {
    icon: Sun,
    title: "Eco-Friendly Operations",
    desc: "Our offices and workshops across 14 UAE locations are designed for energy efficiency, implementing waste reduction and recycling programmes aligned with the UAE's sustainability vision.",
    stat: "14",
    statLabel: "UAE locations optimized",
  },
  {
    icon: Zap,
    title: "Electric Vehicles (EVs)",
    desc: "Eurogulf Mobility Group is accelerating the shift to electric mobility with Tesla and hybrid vehicles in our chauffeur fleet, supporting Dubai's Clean Energy Strategy and the UAE Net Zero 2050 initiative.",
    stat: "EV",
    statLabel: "Fleet expansion underway",
  },
  {
    icon: Users,
    title: "Employee Welfare",
    desc: "ISO 45001:2018 certified occupational health and safety standards for our 1,200+ employees. F.A.I.R. values ensure a respectful, inclusive workplace across all divisions.",
    stat: "1,200+",
    statLabel: "Team members protected",
  },
];

const initiatives = [
  { icon: Leaf, title: "Low-Carbon Fleet", desc: "Integrating hybrid and electric vehicles into our chauffeur and corporate fleet divisions" },
  { icon: Battery, title: "EV Readiness", desc: "Preparing infrastructure at key UAE locations for electric vehicle charging and support" },
  { icon: Recycle, title: "Waste Reduction", desc: "Implementing recycling programmes and reducing single-use materials across all workshops" },
  { icon: Sun, title: "UAE Net Zero 2050", desc: "Aligning operations with the UAE's national strategy for carbon neutrality by 2050" },
];

export default function SustainabilityPage() {
  useEffect(() => { document.title = "Sustainability | Eurogulf Mobility Group | Driving a Greener Future"; }, []);

  return (
    <div data-testid="sustainability-page">
      <BrandHero testid="sustainability-hero" chip="Environmental Responsibility" title="Driving a greener future for UAE mobility."
        lead="At Eurogulf Mobility Group, our journey toward sustainability is more than a goal. It is a commitment to shaping a cleaner, smarter, and more responsible future for mobility."
        image={EV_IMG} imageAlt="Electric vehicle charging under a solar canopy in Dubai" position="center 60%"
        ctas={[{ label: 'Get In Touch', href: '/contact', testid: 'sustainability-hero-cta' }]} />

      <Prose chip="Our Commitment" title="Transport that moves people and protects the environment."
        paragraphs={["We believe that transportation should not only move people but also protect the environment. That's why we are actively integrating eco-conscious practices across our operations, from low-carbon fleet initiatives and solar-powered facilities to the gradual introduction of electric vehicles into our offering."]} />

      {/* FOUR PILLARS */}
      <section data-testid="sustainability-pillars" className="bg-white py-14 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader compact chip="Our Four Pillars" title="A comprehensive approach to sustainable mobility." desc="Across every dimension of our operations." align="center" />
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-px overflow-hidden rounded-xl border border-black/[0.06] bg-black/[0.06]">
            {pillars.map((p, i) => (
              <div key={p.title} data-testid={`pillar-card-${i}`} className="bg-white p-6 lg:p-8 hover:bg-[#FFF4ED]/70 transition-colors">
                <div className="flex items-start gap-5">
                  <span className="grid h-11 w-11 place-items-center rounded-lg bg-[#FFF4ED] text-[#EE5A01] flex-shrink-0"><p.icon className="w-5 h-5" /></span>
                  <div className="flex-1">
                    <h3 className="font-heading font-bold text-lg text-[#121212]">{p.title}</h3>
                    <p className="mt-2 font-body text-sm text-[#666666] leading-relaxed">{p.desc}</p>
                    <div className="mt-5 flex items-baseline gap-2">
                      <span className="font-heading font-black text-2xl text-[#121212] leading-none tracking-tight">{p.stat}</span>
                      <span className="font-body text-xs text-[#666666]">{p.statLabel}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* IMAGE BREAK */}
      <section data-testid="sustainability-solar" className="relative h-[40vh] min-h-[280px] overflow-hidden">
        <img src={SOLAR_IMG} alt="Solar-powered Eurogulf facility in the UAE" loading="lazy" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/45 flex items-center justify-center">
          <div className="text-center px-4">
            <p className="font-heading font-black text-sm tracking-[0.2em] uppercase text-[#EE5A01]">Solar-powered facilities</p>
            <p className="mt-3 font-heading font-bold text-3xl sm:text-5xl text-white tracking-tight">Sustainable by Design</p>
          </div>
        </div>
      </section>

      <FeatureGrid testid="initiatives-section" itemTestId="initiative" chip="Key Initiatives" title="Where we are acting today." cols={4} bg="bg-[#FAFAFA]" items={initiatives} />

      <CtaBand title="Partner with us for a greener future." desc="Join Eurogulf Mobility Group in building sustainable mobility solutions. Contact us to learn how we're making every journey more responsible."
        ctas={[{ label: 'Get In Touch', href: '/contact', testid: 'sustainability-cta' }]} />
    </div>
  );
}
